import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { careerTracks } from '@/mocks/careerData';
import { subjectGroups, trackSubjectMap } from '@/mocks/subjectData';

type SelectedTrack = string | null;
type TabType = '핵심과목' | '일반선택' | '진로선택' | '융합선택';

export default function SubjectGuidePage() {
  const navigate = useNavigate();
  const [selectedTrack, setSelectedTrack] = useState<SelectedTrack>(null);
  const [activeTab, setActiveTab] = useState<TabType>('핵심과목');
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);

  const trackSubjects = selectedTrack ? trackSubjectMap[selectedTrack] : null;

  const getSubjectsForTab = () => {
    if (!selectedTrack) return [];
    const allSubjects = subjectGroups.flatMap(g => g.subjects);

    if (activeTab === '핵심과목') {
      return allSubjects.filter(s => trackSubjects?.core.includes(s.name));
    }
    if (activeTab === '일반선택') {
      return allSubjects.filter(s =>
        s.type === '일반선택' &&
        s.tracks.includes(selectedTrack) &&
        !trackSubjects?.core.includes(s.name)
      );
    }
    if (activeTab === '진로선택') {
      return allSubjects.filter(s =>
        s.type === '진로선택' && s.tracks.includes(selectedTrack)
      );
    }
    if (activeTab === '융합선택') {
      return allSubjects.filter(s =>
        s.type === '융합선택' && s.tracks.includes(selectedTrack)
      );
    }
    return [];
  };

  const selectedSubjectInfo = selectedSubject
    ? subjectGroups.flatMap(g => g.subjects).find(s => s.name === selectedSubject)
    : null;

  const tabs: TabType[] = ['핵심과목', '일반선택', '진로선택', '융합선택'];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Nav */}
      <nav className="bg-background-50 border-b border-background-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="w-8 h-8 flex items-center justify-center text-foreground-600 hover:text-foreground-950 cursor-pointer">
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-accent-500 rounded-lg">
              <i className="ri-book-open-line text-white text-xs"></i>
            </div>
            <span className="font-semibold text-foreground-950 text-sm">고등학교 선택과목 추천</span>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 py-6 md:py-10">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
            계열별 선택과목 안내
          </h1>
          <p className="text-foreground-600 text-sm md:text-base">
            2022 개정 교육과정 기준 · 서울시교육청 선택과목 안내서 완전 반영
          </p>
        </div>

        {/* Track Selection */}
        <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-6">
          <h2 className="font-semibold text-foreground-950 mb-4 text-sm flex items-center gap-2">
            <i className="ri-compass-3-line text-accent-500"></i>
            내 계열을 선택하세요
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
            {careerTracks.map((track) => (
              <button
                key={track.id}
                onClick={() => { setSelectedTrack(track.id); setActiveTab('핵심과목'); setSelectedSubject(null); }}
                className={`whitespace-nowrap py-3 px-2 rounded-xl text-sm font-medium text-center transition-all cursor-pointer ${
                  selectedTrack === track.id
                    ? 'bg-accent-500 text-white shadow-sm'
                    : `${track.bgColor} ${track.color} border border-background-200 hover:border-accent-300`
                }`}
              >
                {track.name}
              </button>
            ))}
          </div>
        </div>

        {!selectedTrack && (
          <div className="text-center py-16 text-foreground-400">
            <i className="ri-book-open-line text-5xl mb-3 block opacity-30"></i>
            <p>계열을 선택하면 맞춤 선택과목을 안내해드립니다</p>
          </div>
        )}

        {selectedTrack && (
          <>
            {/* Tab Navigation */}
            <div className="flex gap-2 mb-5 flex-wrap">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => { setActiveTab(tab); setSelectedSubject(null); }}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                    activeTab === tab
                      ? 'bg-accent-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {tab}
                  <span className="ml-1.5 text-xs opacity-70">({getSubjectsForTab().length || '...'})</span>
                </button>
              ))}
            </div>

            {/* Tab Badge Legend */}
            {activeTab === '핵심과목' && (
              <div className="bg-accent-50 border border-accent-200 rounded-lg p-3 mb-4 flex gap-2">
                <i className="ri-information-line text-accent-500 flex-shrink-0 mt-0.5"></i>
                <p className="text-accent-700 text-xs">
                  <strong>핵심과목</strong>은 해당 계열 진학에 필수적으로 권장되는 과목입니다. 대입 학종 평가에서 핵심과목 이수 여부를 중요하게 평가합니다.
                </p>
              </div>
            )}

            {/* Subjects Grid + Detail */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
              {/* Subject List */}
              <div className="lg:col-span-3">
                <div className="space-y-2">
                  {getSubjectsForTab().map((subject) => (
                    <button
                      key={subject.name}
                      onClick={() => setSelectedSubject(selectedSubject === subject.name ? null : subject.name)}
                      className={`whitespace-normal w-full text-left rounded-xl border p-4 transition-all cursor-pointer ${
                        selectedSubject === subject.name
                          ? 'border-accent-400 bg-accent-50'
                          : 'border-background-200 bg-background-50 hover:border-background-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-semibold text-foreground-950 text-sm">{subject.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              subject.type === '일반선택' ? 'bg-primary-100 text-primary-700' :
                              subject.type === '진로선택' ? 'bg-accent-100 text-accent-700' :
                              'bg-secondary-100 text-secondary-700'
                            }`}>
                              {subject.type}
                            </span>
                            {activeTab === '핵심과목' && (
                              <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">필수</span>
                            )}
                          </div>
                          <p className="text-foreground-500 text-xs leading-relaxed">{subject.description}</p>
                        </div>
                        <i className={`ri-arrow-right-s-line text-foreground-400 flex-shrink-0 mt-0.5 transition-transform ${
                          selectedSubject === subject.name ? 'rotate-90 text-accent-500' : ''
                        }`}></i>
                      </div>
                    </button>
                  ))}

                  {getSubjectsForTab().length === 0 && (
                    <div className="text-center py-10 text-foreground-400 text-sm">
                      해당 탭에 표시할 과목이 없습니다
                    </div>
                  )}
                </div>
              </div>

              {/* Subject Detail */}
              <div className="lg:col-span-2">
                {selectedSubjectInfo ? (
                  <div className="bg-background-50 border border-accent-200 rounded-xl p-5 sticky top-24">
                    <h3 className="font-bold text-foreground-950 mb-1">{selectedSubjectInfo.name}</h3>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        selectedSubjectInfo.type === '일반선택' ? 'bg-primary-100 text-primary-700' :
                        selectedSubjectInfo.type === '진로선택' ? 'bg-accent-100 text-accent-700' :
                        'bg-secondary-100 text-secondary-700'
                      }`}>{selectedSubjectInfo.type}</span>
                      <span className="text-xs text-foreground-500">{selectedSubjectInfo.credits}학점</span>
                    </div>
                    <p className="text-foreground-600 text-sm mb-4 leading-relaxed">{selectedSubjectInfo.description}</p>

                    <div className="mb-4">
                      <div className="text-xs font-semibold text-foreground-700 mb-2">관련 직업</div>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedSubjectInfo.relatedJobs.map(job => (
                          <span key={job} className="text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full">{job}</span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs font-semibold text-foreground-700 mb-2">관련 학과</div>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedSubjectInfo.relatedMajors.map(major => (
                          <span key={major} className="text-xs bg-background-100 text-foreground-600 px-2 py-0.5 rounded-full">{major}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-background-100 border border-background-200 rounded-xl p-6 text-center sticky top-24">
                    <i className="ri-bookmark-line text-3xl text-background-400 block mb-2"></i>
                    <p className="text-foreground-500 text-sm">과목을 클릭하면<br />상세 정보가 표시됩니다</p>
                  </div>
                )}
              </div>
            </div>

            {/* 3-Year Roadmap Tip */}
            <div className="mt-8 bg-primary-50 border border-primary-200 rounded-xl p-5">
              <h3 className="font-semibold text-primary-800 mb-3 flex items-center gap-2">
                <i className="ri-map-2-line"></i>
                3년 선택과목 로드맵 (참고)
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { year: '고1', desc: '공통과목 이수 + 기초 탐색', tip: '핵심과목 파악, 진로 방향 탐색' },
                  { year: '고2', desc: '일반·진로 선택과목 집중', tip: '계열 관련 일반선택 + 진로선택 선택' },
                  { year: '고3', desc: '심화 진로선택 + 융합', tip: '전공 관련 심화 과목으로 전문성 어필' },
                ].map((item) => (
                  <div key={item.year} className="bg-white border border-primary-100 rounded-xl p-3">
                    <div className="font-bold text-primary-600 mb-1">{item.year}</div>
                    <div className="text-xs font-medium text-foreground-700 mb-1">{item.desc}</div>
                    <div className="text-xs text-foreground-500">{item.tip}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}